<script setup lang="ts">
import type {
  FoundResponse,
  LocationInput,
  PublicBottle,
  PublicBottleResponse,
  ReportType,
} from '@wilson/shared'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import LocationField from '../components/LocationField.vue'
import PublicHeader from '../components/PublicHeader.vue'
import { api, finderApi } from '../services/api'
import { getClaim, recoveryLink, saveClaim, type StoredClaim } from '../services/claims'

const route = useRoute()
const bottleId = computed(() => String(route.params.id))
const bottle = ref<PublicBottle | null>(null)
const unavailable = ref(false)
const claim = ref<StoredClaim | null>(null)
const loading = ref(true)
const busy = ref(false)
const error = ref('')
const notice = ref('')
const commentMessage = ref('')
const commentLocation = ref<LocationInput>()
const reportType = ref<ReportType>('bottle_damaged')
const reportMessage = ref('')
const showReport = ref(false)
const formStartedAt = Date.now()

const canFind = computed(
  () => bottle.value?.availability === 'active' && bottle.value.status === 'hidden' && !claim.value,
)

async function load() {
  loading.value = true
  error.value = ''
  unavailable.value = false
  try {
    const response = await api<PublicBottleResponse>(`/bottles/${bottleId.value}`)
    if (response.availability === 'unavailable') {
      bottle.value = null
      unavailable.value = true
    } else {
      bottle.value = response
    }
    claim.value = getClaim(bottleId.value)
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'This bottle could not be loaded.'
  } finally {
    loading.value = false
  }
}

async function markFound() {
  busy.value = true
  error.value = ''
  try {
    const result = await api<FoundResponse>(`/bottles/${bottleId.value}/found`, { method: 'POST' })
    saveClaim(bottleId.value, result)
    claim.value = getClaim(bottleId.value)
    notice.value =
      'You are now this bottle’s temporary keeper. Save your re-hide link before moving on.'
    await load()
  } catch (caught) {
    error.value =
      caught instanceof Error ? caught.message : 'The bottle could not be marked as found.'
  } finally {
    busy.value = false
  }
}

async function copyLink() {
  if (!claim.value) return
  await navigator.clipboard.writeText(recoveryLink(bottleId.value, claim.value.token))
  notice.value = 'Re-hide link copied. Keep it private—it grants temporary control of this bottle.'
}

async function submitComment() {
  if (!claim.value) return
  busy.value = true
  error.value = ''
  try {
    await finderApi(bottleId.value, claim.value.token, '/comments', {
      message: commentMessage.value,
      location: commentLocation.value,
      website: '',
      formStartedAt,
    })
    commentMessage.value = ''
    commentLocation.value = undefined
    notice.value = 'Your anonymous note is now part of the journey.'
    await load()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Your note could not be added.'
  } finally {
    busy.value = false
  }
}

async function submitReport() {
  busy.value = true
  error.value = ''
  try {
    await api(`/bottles/${bottleId.value}/reports`, {
      method: 'POST',
      body: JSON.stringify({
        type: reportType.value,
        message: reportMessage.value || undefined,
        website: '',
        formStartedAt,
      }),
      headers: { 'content-type': 'application/json' },
    })
    showReport.value = false
    reportMessage.value = ''
    notice.value = 'Thank you. The administrator will review your report.'
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'The report could not be sent.'
  } finally {
    busy.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="public-page bottle-page-bg">
    <PublicHeader />
    <main class="bottle-main">
      <div v-if="loading" class="state-card">Following the trail…</div>
      <div v-else-if="error && !bottle && !unavailable" class="state-card error-state">
        <span class="state-icon">?</span>
        <h1>Bottle not found</h1>
        <p>{{ error }}</p>
        <RouterLink class="button secondary" to="/">Return home</RouterLink>
      </div>
      <template v-else>
        <p v-if="notice" class="notice" role="status">{{ notice }}</p>
        <p v-if="error" class="form-error banner" role="alert">{{ error }}</p>

        <section v-if="unavailable" class="state-card unavailable-state">
          <span class="state-icon" aria-hidden="true">—</span>
          <h1>This journey is unavailable</h1>
          <p>
            This bottle is not currently sharing its public details. You can still report a safety,
            damage, missing-bottle, or other concern below.
          </p>
        </section>

        <template v-else-if="bottle">
          <section class="bottle-intro">
            <div>
              <p class="eyebrow">Bottle passport · {{ bottle.status }}</p>
              <h1>{{ bottle.name }}</h1>
              <p class="quote-mark">“{{ bottle.quote.text }}”</p>
              <p class="quote-name">Message: {{ bottle.quote.name }}</p>
            </div>
            <div class="passport-stamp">
              <strong>{{ bottle.hiddenCount }}</strong>
              <span>times hidden</span>
            </div>
          </section>

          <section v-if="bottle.availability === 'read_only'" class="availability-notice">
            <strong>This journey is currently read-only.</strong>
            <span>Its story remains visible, but new finds and traveller notes are paused.</span>
          </section>

          <section v-if="canFind" class="found-card">
            <div>
              <p class="eyebrow">You found it!</p>
              <h2>Ready to add the next chapter?</h2>
              <p>
                Marking it found gives this browser one temporary, anonymous permission to comment
                and re-hide it.
              </p>
            </div>
            <button class="button primary" :disabled="busy" @click="markFound">
              {{ busy ? 'Claiming…' : 'I found this bottle' }}
            </button>
          </section>

          <section v-else-if="claim" class="keeper-card">
            <div>
              <p class="eyebrow">Temporary keeper pass</p>
              <h2>The bottle is in your hands</h2>
              <p>
                Save this private link. Your permission ends as soon as the bottle is re-hidden.
              </p>
            </div>
            <div class="button-row">
              <button class="button secondary" @click="copyLink">Copy re-hide link</button
              ><RouterLink class="button primary" :to="`/b/${bottle.id}/rehide`"
                >Re-hide bottle</RouterLink
              >
            </div>
          </section>

          <section class="messages-section">
            <div class="section-heading">
              <div>
                <p class="eyebrow">Notes from the trail</p>
                <h2>Messages from finders</h2>
              </div>
            </div>
            <div v-if="bottle.comments.length" class="message-grid">
              <article v-for="message in bottle.comments" :key="message.id" class="message-card">
                <p>“{{ message.message }}”</p>
                <footer>
                  {{ new Date(message.createdAt).toLocaleDateString()
                  }}<template v-if="message.publicLocation">
                    · {{ message.publicLocation }}</template
                  >
                </footer>
              </article>
            </div>
            <p v-else class="empty-copy">No notes yet. The first one could be yours.</p>
          </section>

          <section v-if="claim && bottle.availability === 'active'" class="form-card">
            <p class="eyebrow">Leave something for the next finder</p>
            <h2>Add an anonymous note</h2>
            <form @submit.prevent="submitComment">
              <label
                >Your message<textarea
                  v-model="commentMessage"
                  required
                  minlength="1"
                  maxlength="2000"
                  rows="4"
                  placeholder="A kind thought, a small story, or where this message found you…"
                />
              </label>
              <LocationField v-model="commentLocation" />
              <button class="button primary" :disabled="busy || !commentMessage.trim()">
                {{ busy ? 'Sending…' : 'Add to the journey' }}
              </button>
            </form>
          </section>

          <div class="content-grid">
            <section class="journey-card">
              <div class="section-heading">
                <div>
                  <p class="eyebrow">The voyage so far</p>
                  <h2>Journey log</h2>
                </div>
                <span>{{ bottle.journey.length }} events</span>
              </div>
              <ol class="timeline">
                <li v-for="movement in bottle.journey" :key="movement.id">
                  <span class="timeline-dot"></span>
                  <div>
                    <strong>{{ movement.type.replaceAll('_', ' ') }}</strong>
                    <p>
                      {{
                        new Date(movement.occurredAt).toLocaleDateString(undefined, {
                          dateStyle: 'medium',
                        })
                      }}<template v-if="movement.publicLocation">
                        · {{ movement.publicLocation }}</template
                      >
                    </p>
                  </div>
                </li>
              </ol>
            </section>

            <aside class="stats-stack">
              <article>
                <strong>{{ bottle.quoteBottleCount }}</strong
                ><span>bottles carry this message</span>
              </article>
              <article>
                <strong>{{ bottle.comments.length }}</strong
                ><span>visible traveller notes</span>
              </article>
              <article>
                <strong>{{
                  bottle.journeyStartedAt
                    ? new Date(bottle.journeyStartedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'Not yet'
                }}</strong
                ><span>journey began</span>
              </article>
            </aside>
          </div>
        </template>

        <section v-if="bottle || unavailable" class="report-strip">
          <div>
            <strong>Something not right?</strong
            ><span
              >Report damage, a quote mismatch, an unsafe location, or an inappropriate
              message.</span
            >
          </div>
          <button class="text-button" @click="showReport = !showReport">
            {{ showReport ? 'Close report' : 'Report an issue' }}
          </button>
        </section>
        <form
          v-if="(bottle || unavailable) && showReport"
          class="form-card compact"
          @submit.prevent="submitReport"
        >
          <label
            >Issue type<select v-model="reportType">
              <option value="quote_mismatch">Physical quote does not match</option>
              <option value="bottle_damaged">Bottle damaged</option>
              <option value="bottle_missing">Bottle missing</option>
              <option value="inappropriate_message">Inappropriate message</option>
              <option value="unsafe_location">Unsafe location</option>
              <option value="other">Other</option>
            </select></label
          >
          <label
            >Details <span class="optional">optional</span
            ><textarea v-model="reportMessage" maxlength="2000" rows="3" />
          </label>
          <button class="button danger" :disabled="busy">Send report</button>
        </form>
      </template>
    </main>
  </div>
</template>
